import React from 'react'
import styled, { css } from 'styled-components'
import { ButtonStyleProps, MarginProps, variant } from 'styled-system'

import Box from 'components/Box'

import { Button, BaseButtonProps } from '../Button'
import Tooltip, { PositionType } from '../../Tooltip'
import * as baseVariants from '../Button/variants'
import * as variants from './variants'

export type IconButtonVariant =
  | 'fill'
  | 'outline'
  | 'ghost'
  | 'success'
  | 'criticalOutline'
  | 'criticalGhost'
  | 'blandGhost'
  | 'blandOutline'
  | 'dashedCircleGray'
  | 'dashedCircleBlue'
  | 'xButtonRed'
  | 'xButtonRedV2'
  | 'checkButtonGreen'
  | 'checkButtonGreenV2'

type IconButtonProps = BaseButtonProps &
  MarginProps & {
    badge?: boolean
    isSubmitting?: boolean
    tooltip?: string
    tooltipPosition?: PositionType
    variant?: IconButtonVariant
    width?: string | number
    height?: string | number
    zIndex?: number
  }

const ICON_SIZE = 14

const IconButton = (props: IconButtonProps) => (
  <Button {...props} iconPosition="center" iconSize={props.iconSize || ICON_SIZE} margin={0} />
)

const IconButtonWithTooltip = React.forwardRef<HTMLDivElement, IconButtonProps>(
  (
    { zIndex, badge, tooltip, tooltipPosition, variant = 'fill', m, margin, marginX, mb, ml, mr, mt, my, ...props },
    ref,
  ) => {
    // Excluded `mx` from this object, it breaks the margin application for some reason
    //  Tried using `Box` here too but it still breaks
    //  Even adding it separately breaks
    const marginProps = { m, margin, marginX, mb, ml, mr, mt, my }

    return (
      <Box {...marginProps} ref={ref}>
        {tooltip ? (
          <Tooltip zIndex={zIndex} badge={badge ?? false} title={tooltip} position={tooltipPosition ?? 'top'}>
            <IconButton {...props} variant={variant} />
          </Tooltip>
        ) : (
          <IconButton {...props} variant={variant} />
        )}
      </Box>
    )
  },
)

const IconButtonVariants = styled(IconButtonWithTooltip)<ButtonStyleProps & { zIndex?: number }>((p) => {
  const sideLength =
    p.variant === 'dashedCircleGray' ||
    p.variant === 'dashedCircleBlue' ||
    p.variant === 'xButtonRed' ||
    p.variant === 'xButtonRedV2' ||
    p.variant === 'checkButtonGreenV2' ||
    p.variant === 'checkButtonGreen'
      ? p.size === 'xsmall'
        ? '24px'
        : p.size === 'small'
        ? '32px'
        : '40px'
      : 'auto'

  const width = p.width ? (typeof p.width === 'string' ? p.width : `${p.width}px`) : sideLength
  const height = p.height ? (typeof p.height === 'string' ? p.height : `${p.height}px`) : sideLength

  return css(
    {
      ...baseVariants.base,
      fontSize: p.iconSize || ICON_SIZE,
      padding: p.size === 'small' || p.size === 'xsmall' ? '0' : '0.5em',
      width,
      height,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '24px',
      minHeight: '24px',
      zIndex: p.zIndex,
    },
    variant({ variants: { ...baseVariants, ...variants } }),
  )
})

export default IconButtonVariants
